<template lang="pug">
	//- Проигрыватель ассета Spine: анимация скелета с выбранным скином (рисуется рантаймом spine-canvas в canvas).
	//- Используется в свойствах Spine ассета, во всплывающем превью ассета и в ИИ-чате
	.spine-player(:style="{width: width + 'px', height: height + 'px'}")
		canvas(ref="canvas" v-show="!error")
		i.fa.fa-child.spine-player-fallback(v-if="error" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: error}")
</template>

<script>
	// Доля размера проигрывателя, которую занимает скелет (остальное - отступы)
	const SKELETON_FILL = 0.9;
	// Анимация по умолчанию: первая анимация с таким названием, иначе первая анимация скелета
	const PREFERRED_ANIMATION_REGEXP = /idle/i;
	// Сколько кадров анимации проверяется, чтобы найти общие границы скелета за всю анимацию (чтобы анимация не выходила за canvas)
	const BOUNDS_SAMPLES = 12;
	// Максимальный шаг анимации за кадр в секундах (после неактивной вкладки анимация не "перепрыгивает")
	const MAX_FRAME_DELTA = 0.1;

	// Рантайм Spine загружается один раз на страницу отдельным скриптом (сервер копирует его из npm пакета в Public, см. Asset.copySpineRuntime)
	let spineRuntimePromise = null;

	function loadSpineRuntime() {

		if (!spineRuntimePromise) spineRuntimePromise = new Promise((resolve, reject) => {

			if (window.spine?.SkeletonRenderer) return resolve(window.spine);

			let script = document.createElement('script');

			script.src = `${location.protocol}//${SetupSettings["domain"]}/spine-canvas.min.js`;
			script.onload = () => window.spine?.SkeletonRenderer ? resolve(window.spine) : reject(new Error('Spine runtime is not available'));
			script.onerror = () => {

				// Следующий проигрыватель попробует загрузить рантайм ещё раз
				spineRuntimePromise = null;

				reject(new Error('Spine runtime loading error'));

			};

			document.head.appendChild(script);

		});

		return spineRuntimePromise;

	}

	function loadImage(url) {

		return new Promise((resolve, reject) => {

			let image = new Image();

			image.onload = () => resolve(image);
			image.onerror = () => reject(new Error('the texture can not be loaded'));
			image.src = url;

		});

	}

	// Разобранные скелеты Spine, общие для всех проигрывателей страницы: разбор атласа и json, а также расчёт границ анимаций -
	// самые долгие операции, поэтому при повторном показе (повторное наведение на ассет, раскрытие его свойств) скелет берётся готовым.
	// Ключ верхнего уровня - ключ ассета (props cacheKey) или сами данные скелета (у объекта - ссылка, у текста - значение),
	// ключ нижнего уровня - url текстуры (у скелета может быть несколько страниц атласа)
	const SPINE_SKELETONS_LIMIT = 12;

	let spineSkeletons = new Map();

	function cacheSpineSkeleton(key, imageUrl, promise) {

		let byImageUrl = spineSkeletons.get(key);

		if (!byImageUrl) {

			byImageUrl = new Map();
			spineSkeletons.set(key, byImageUrl);

			// Вытесняем самый старый скелет, чтобы текстуры всех просмотренных ассетов не оставались в памяти (Map хранит ключи в порядке добавления)
			if (spineSkeletons.size > SPINE_SKELETONS_LIMIT) spineSkeletons.delete(spineSkeletons.keys().next().value);

		}

		byImageUrl.set(imageUrl, promise);

	}

	// Разбор скелета выполняется один раз на ассет: параллельные проигрыватели (миниатюра ассета и его свойства) ждут одну загрузку
	function loadSpineSkeleton(key, {json, atlas: atlasText, imageUrl}) {

		let skeleton = spineSkeletons.get(key)?.get(imageUrl);

		if (skeleton) return skeleton;

		skeleton = (async () => {

			let [spine, image] = await Promise.all([loadSpineRuntime(), loadImage(imageUrl)]);

			let atlas = new spine.TextureAtlas(atlasText);

			// У ассета Spine одна страница атласа - файл ассета (имя страницы в тексте атласа не совпадает с url ассета)
			_.each(atlas.pages, page => page.setTexture(new spine.CanvasTexture(image)));

			let skeletonData = new spine.SkeletonJson(new spine.AtlasAttachmentLoader(atlas)).readSkeletonData(_.isString(json) ? JSON.parse(json) : json);

			return {
				spine,
				skeletonData,
				// Отдельный скелет для расчёта границ анимаций - его поза и скин не мешают анимации проигрывателя
				measureSkeleton: new spine.Skeleton(skeletonData),
				bounds: {}
			};

		})();

		// Ошибку не кэшируем - следующий проигрыватель попробует разобрать скелет ещё раз
		skeleton.catch(() => {

			let byImageUrl = spineSkeletons.get(key);

			if (!byImageUrl) return;

			byImageUrl.delete(imageUrl);

			if (!byImageUrl.size) spineSkeletons.delete(key);

		});

		cacheSpineSkeleton(key, imageUrl, skeleton);

		return skeleton;

	}

	export default {
		name: "SpinePlayer",
		// json - данные скелета (объект или текст .json), atlas - текст .atlas, imageUrl - текстура атласа (.png).
		// animation, skin - имена анимации и скина (null - анимация "idle" или первая, скин по умолчанию), playing - false показывает первый кадр на паузе.
		// cacheKey - ключ ассета для общего кэша разобранных скелетов (Asset.getSpineCacheKey): ассеты с одинаковым ключом разбираются один раз
		props: {
			json: {required: true},
			atlas: {type: String, required: true},
			imageUrl: {type: String, required: true},
			cacheKey: {type: String, default: null},
			width: {type: Number, default: 120},
			height: {type: Number, default: 90},
			animation: {type: String, default: null},
			skin: {type: String, default: null},
			loop: {type: Boolean, default: true},
			playing: {type: Boolean, default: true}
		},
		data() {

			this.renderId = 0;

			this.resources = null;
			this.spine = null;
			this.skeleton = null;
			this.animationState = null;
			this.bounds = null;
			this.renderer = null;

			this.frameRequest = null;
			this.lastFrameTime = 0;
			this.visible = true;

			return {
				error: null
			};

		},
		watch: {
			json() {

				this.load();

			},
			atlas() {

				this.load();

			},
			imageUrl() {

				this.load();

			},
			animation() {

				this.restart();

			},
			skin() {

				this.restart();

			},
			loop() {

				this.restart();

			},
			playing(playing) {

				if (playing) this.start();
				else this.stop();

			},
			width() {

				this.draw();

			},
			height() {

				this.draw();

			}
		},
		mounted() {

			// Анимация не проигрывается, пока проигрыватель не виден (например, сообщение чата прокручено за пределы экрана)
			this.visibilityObserver = new IntersectionObserver(entries => {

				this.visible = entries[0].isIntersecting;

				if (this.visible) this.start();
				else this.stop();

			});

			this.visibilityObserver.observe(this.$el);

			this.load();

		},
		beforeDestroy() {

			this.renderId++;

			this.stop();

			this.visibilityObserver.disconnect();

		},
		methods: {
			async load() {

				// Если данные сменились во время загрузки - показываем только последний вариант
				let renderId = ++this.renderId;

				this.stop();

				try {

					// Скелет берётся из общего кэша - повторный показ (например, повторное наведение на ассет) не разбирает его с нуля
					let resources = await loadSpineSkeleton(this.cacheKey || this.json, {json: this.json, atlas: this.atlas, imageUrl: this.imageUrl});

					if (renderId !== this.renderId || !this.$refs.canvas) return;

					this.resources = resources;
					this.spine = resources.spine;
					this.skeleton = new resources.spine.Skeleton(resources.skeletonData);
					this.animationState = new resources.spine.AnimationState(new resources.spine.AnimationStateData(resources.skeletonData));

					this.applySelection();

					this.error = null;

					let skeletonData = this.skeleton.data;

					this.$emit('loaded', {
						animations: _.map(skeletonData.animations, animation => ({name: animation.name, duration: animation.duration})),
						skins: _.map(skeletonData.skins, 'name'),
						animation: this.currentAnimationName,
						skin: this.currentSkinName
					});

					this.start();

				} catch (e) {

					if (renderId !== this.renderId) return;

					this.setError(e);

				}

			},
			// Применяет скин и запускает анимацию с начала (props skin, animation, loop)
			applySelection() {

				let skeleton = this.skeleton,
					skeletonData = skeleton.data;

				let skin = (this.skin && skeletonData.findSkin(this.skin)) || this.getDefaultSkin(skeletonData);

				// Скин по умолчанию используется рантаймом и без setSkin - после смены скина вложения слотов сбрасываются к setup pose
				skeleton.setSkin(skin === skeletonData.defaultSkin ? null : skin);
				skeleton.setToSetupPose();

				let animation = (this.animation && skeletonData.findAnimation(this.animation))
					|| _.find(skeletonData.animations, animation => PREFERRED_ANIMATION_REGEXP.test(animation.name))
					|| skeletonData.animations[0];

				this.currentSkinName = skin?.name || null;
				this.currentAnimationName = animation?.name || null;

				this.bounds = this.calculateBounds(animation, skin);

				this.animationState.clearTracks();

				if (animation) this.animationState.setAnimation(0, animation.name, this.loop);

				this.updatePose(0);
				this.draw();

			},
			// Скин по умолчанию бывает пустым (все вложения лежат в именованных скинах) - тогда берётся первый именованный скин
			getDefaultSkin(skeletonData) {

				let defaultSkin = skeletonData.defaultSkin;

				if (defaultSkin && defaultSkin.getAttachments().length) return defaultSkin;

				return _.find(skeletonData.skins, skin => skin !== defaultSkin) || defaultSkin;

			},
			// Общие границы скелета за всю анимацию в мировых координатах Spine: {minX, minY, maxX, maxY}
			calculateBounds(animation, skin) {

				let name = animation?.name || null,
					// Границы зависят от скина (вложения слотов) - запоминаем их для пары скин + анимация, чтобы не пересчитывать при каждом показе превью
					cacheKey = name ? `${skin?.name || ''}|${name}` : null;

				if (cacheKey && this.resources.bounds[cacheKey]) return this.resources.bounds[cacheKey];

				let spine = this.spine,
					// Измеряем по общему скелету из кэша: он выставляется заново на время расчёта и не портит позу скелета проигрывателя
					skeleton = this.resources.measureSkeleton,
					offset = new spine.Vector2(),
					size = new spine.Vector2(),
					bounds = null;

				skeleton.setSkin(skin === skeleton.data.defaultSkin ? null : skin);

				let times = animation && animation.duration > 0 ? _.times(BOUNDS_SAMPLES + 1, index => animation.duration * index / BOUNDS_SAMPLES) : [0];

				_.each(times, time => {

					skeleton.setToSetupPose();

					if (animation) animation.apply(skeleton, time, time, false, [], 1, spine.MixBlend.setup, spine.MixDirection.mixIn);

					skeleton.updateWorldTransform(spine.Physics.reset);
					skeleton.getBounds(offset, size, []);

					if (!(size.x > 0 && size.y > 0) || !_.isFinite(offset.x) || !_.isFinite(offset.y)) return;

					bounds = {
						minX: Math.min(bounds ? bounds.minX : Infinity, offset.x),
						minY: Math.min(bounds ? bounds.minY : Infinity, offset.y),
						maxX: Math.max(bounds ? bounds.maxX : -Infinity, offset.x + size.x),
						maxY: Math.max(bounds ? bounds.maxY : -Infinity, offset.y + size.y)
					};

				});

				skeleton.setToSetupPose();

				if (!bounds) throw new Error('the skeleton has no visible attachments');

				if (cacheKey) this.resources.bounds[cacheKey] = bounds;

				return bounds;

			},
			updatePose(delta) {

				let spine = this.spine,
					skeleton = this.skeleton;

				this.animationState.update(delta);
				this.animationState.apply(skeleton);

				skeleton.update(delta);
				skeleton.updateWorldTransform(spine.Physics.update);

			},
			draw() {

				let canvas = this.$refs.canvas;

				if (!this.skeleton || !this.bounds || !canvas) return;

				let pixelRatio = window.devicePixelRatio || 1,
					canvasWidth = Math.round(this.width * pixelRatio),
					canvasHeight = Math.round(this.height * pixelRatio);

				// Размер canvas меняется только при изменении размеров - присваивание width / height каждый кадр пересоздаёт буфер
				if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {

					canvas.width = canvasWidth;
					canvas.height = canvasHeight;
					canvas.style.width = this.width + 'px';
					canvas.style.height = this.height + 'px';

				}

				let bounds = this.bounds,
					boundsWidth = bounds.maxX - bounds.minX,
					boundsHeight = bounds.maxY - bounds.minY;

				let context = canvas.getContext('2d'),
					scale = Math.min(canvasWidth / boundsWidth, canvasHeight / boundsHeight) * SKELETON_FILL;

				context.setTransform(1, 0, 0, 1, 0, 0);
				context.clearRect(0, 0, canvasWidth, canvasHeight);

				// Мировые координаты Spine направлены вверх по Y - переворачиваем ось и вписываем границы анимации в центр canvas
				context.translate(canvasWidth / 2, canvasHeight / 2);
				context.scale(scale, -scale);
				context.translate(-(bounds.minX + boundsWidth / 2), -(bounds.minY + boundsHeight / 2));

				if (!this.renderer || this.renderer.ctx !== context) {

					this.renderer = new this.spine.SkeletonRenderer(context);

					// Отрисовка треугольниками нужна для mesh вложений (обычная отрисовка картинками рисует только region вложения)
					this.renderer.triangleRendering = true;

				}

				this.renderer.draw(this.skeleton);

			},
			start() {

				if (!this.playing || !this.visible || !this.skeleton || this.error || this.frameRequest) return;

				this.lastFrameTime = performance.now();
				this.frameRequest = requestAnimationFrame(time => this.frame(time));

			},
			frame(time) {

				let delta = Math.min(Math.max(time - this.lastFrameTime, 0) / 1000, MAX_FRAME_DELTA);

				this.lastFrameTime = time;

				this.updatePose(delta);
				this.draw();

				this.frameRequest = requestAnimationFrame(time => this.frame(time));

			},
			stop() {

				if (this.frameRequest) cancelAnimationFrame(this.frameRequest);

				this.frameRequest = null;

			},
			// Перезапуск анимации с начала с текущими skin / animation / loop (также вызывается снаружи через ref - повторный запуск той же анимации)
			restart() {

				if (!this.skeleton) return;

				try {

					this.applySelection();

					this.error = null;

					this.start();

				} catch (e) {

					this.stop();

					this.setError(e);

				}

			},
			setError(e) {

				this.error = `Spine preview is not available: ${e?.message || e}`;

				this.$emit('error', e);

			}
		}
	}
</script>

<style lang="sass">

	.spine-player
		display: flex
		align-items: center
		justify-content: center

		canvas
			display: block

		.spine-player-fallback
			font-size: 24px
			color: #73818f

</style>
